import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { save } from "./api-aluno-turma";
import { addAlunosTurma, updateAlunosTurma, setAlunoTurma as setAlunoTurmaRedux } from "./actions/actions";
import { APROVADO, REPROVADO_POR_DESEMPENHO, REPROVADO_POR_FREQUENCIA, resultadoFinalText } from "./ResultadoFinal";
import AlunoFindDialog from "../Aluno/AlunoFindDialog";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../util/ToastUtil";
import TextFieldDate from "../UI/TextFieldDate";

const RESULTADOS = [APROVADO, REPROVADO_POR_FREQUENCIA, REPROVADO_POR_DESEMPENHO];

const SAVE_ERRORS = {
  "Erro: Bimestre nao cadastrado":
    "A série desta turma não possui bimestres cadastrados. Cadastre os bimestres antes de inserir alunos na turma.",
  "Erro: Aluno Turma já cadastrado": "Aluno já cadastrado na turma.",
};

export default function AlunoTurmaForm() {
  const dispatch = useDispatch();
  const alunoTurmaRedux = useSelector((state) => state.alunoTurma);
  const [alunoTurma, setAlunoTurma] = useState(alunoTurmaRedux);
  const [openSearch, setOpenSearch] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAlunoTurma(alunoTurmaRedux);
  }, [alunoTurmaRedux]);

  if (!alunoTurma) return null;
  const eNovo = alunoTurma.eNovo;

  const updateField = (name, value) => {
    setAlunoTurma((prevState) => ({ ...prevState, [name]: value }));
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!alunoTurma.aluno) {
      errorToast("Selecione o aluno.");
      return;
    }
    if (!alunoTurma.dataEntrada) {
      errorToast("Preencha a data de entrada.");
      return;
    }

    setIsSaving(true);
    save(alunoTurma)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(SAVE_ERRORS[data.message] ?? data.message);
          return;
        }
        success("Salvo com sucesso.");
        dispatch(eNovo ? addAlunosTurma(data) : updateAlunosTurma(data));
        dispatch(setAlunoTurmaRedux(data));
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <>
      <AlunoFindDialog
        openSearch={openSearch}
        setOpenSearch={setOpenSearch}
        setAluno={(aluno) => updateField("aluno", aluno)}
      />
      <Stack component="form" onSubmit={clickSubmit} spacing={2.5}>
        {eNovo ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              required
              label="Aluno"
              placeholder="Selecione um aluno"
              value={alunoTurma.aluno?.nome ?? ""}
              onClick={() => setOpenSearch(true)}
              slotProps={{ input: { readOnly: true } }}
            />
            <Button
              variant="outlined"
              startIcon={<PersonSearchOutlinedIcon />}
              onClick={() => setOpenSearch(true)}
              sx={{ flexShrink: 0 }}
            >
              Buscar
            </Button>
          </Stack>
        ) : (
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {alunoTurma.aluno?.nome}
            </Typography>
            {alunoTurma.aluno?.codAlunoEscola && (
              <Typography variant="body2" color="text.secondary">
                Código {alunoTurma.aluno.codAlunoEscola}
              </Typography>
            )}
          </Box>
        )}

        <Grid container spacing={2}>
          {!eNovo && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                size="small"
                fullWidth
                required
                label="Nº Chamada"
                value={alunoTurma.numeroChamada ?? ""}
                onChange={(event) => updateField("numeroChamada", event.target.value.replace(/\D/g, ""))}
                slotProps={{ htmlInput: { inputMode: "numeric" } }}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: eNovo ? 6 : 4.5 }}>
            <TextFieldDate
              label="Data Entrada"
              value={alunoTurma.dataEntrada || null}
              onChange={(value) => updateField("dataEntrada", value ? dayjs(value) : null)}
              slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
            />
          </Grid>
          {!eNovo && (
            <Grid size={{ xs: 12, sm: 4.5 }}>
              <TextFieldDate
                label="Data Saída"
                value={alunoTurma.dataSaida || null}
                onChange={(value) => updateField("dataSaida", value ? dayjs(value) : null)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              size="small"
              fullWidth
              label="Resultado Final"
              value={alunoTurma.resultadoFinalEnum ?? ""}
              onChange={(event) => updateField("resultadoFinalEnum", event.target.value || null)}
            >
              <MenuItem value="">
                <em>Sem resultado</em>
              </MenuItem>
              {RESULTADOS.map((rf) => (
                <MenuItem key={rf} value={rf}>
                  {resultadoFinalText(rf)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {!eNovo && (
            <Grid size={{ xs: 12, sm: 6 }} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch checked={Boolean(alunoTurma.ativo)} onChange={(event) => updateField("ativo", event.target.checked)} />
                }
                label="Ativo na turma"
              />
            </Grid>
          )}
        </Grid>

        <Box>
          <Button
            type="submit"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
            disabled={isSaving}
          >
            Salvar
          </Button>
        </Box>
      </Stack>
    </>
  );
}
